'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { DonationBnb } from '@/app/_components/FloatingBnbController/_components/DonationBnb';
import { GoodsDonationBnb } from '@/app/_components/FloatingBnbController/_components/GoodsDonationBnb';
import { getFloatingBnbKind } from '@/app/_components/FloatingBnbController/constants';
import { GlobalNavigation } from '@/app/_components/GlobalNavigation';
import { hasGoodsDonationHome } from '@/constants/yearEndDonationHome';
import { useCheckKakaobank } from '@/hooks/useCheckKakaobank';
import { usePartnerChromeClassName } from '@/hooks/usePartnerChromeClassName';
import { useRenderedPathname } from '@/hooks/useRenderedPathname';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { hasWelloBottomNav } from '@/utils';

//* 크로스페이드 시간 = 퇴장 레이어 언마운트 유예. 셸·레이어가 같은 시계를 쓴다.
const CROSSFADE_DURATION = 200;
type BottomNavState = 'wello' | 'donation' | 'goods-donation' | 'none';
type VisibleNavState = Exclude<BottomNavState, 'none'>;
const WELLO_TAB_BAR_SHAPE_CLASS_NAME = 'owns-safe-area inset-x-0 bottom-0 h-[calc(70px+var(--safe-area-bottom,0px))] rounded-none border-Gray-50 shadow-none';
//* 셸은 위치만 잡고 투명하게 둔다 — 알약이 스크롤로 줄어도 셸 높이가 고정이라 하단 인셋이 흔들리지 않는다
const FLOATING_SHAPE_CLASS_NAME = 'pointer-events-none inset-x-20 bottom-[calc(10px+var(--safe-area-bottom,0px))] h-52 border-transparent bg-transparent';
interface BottomNavLayerProps {
  isActive: boolean;
  isEntering: boolean;
  className?: string;
  children: ReactNode;
}
const BottomNavLayer = t0 => {
  const $ = _c(8);
  const {
    isActive,
    isEntering,
    className,
    children
  } = t0;
  const t1 = !isActive || undefined;
  const t2 = isEntering && "animate-in fade-in-0 motion-reduce:animate-none";
  const t3 = isActive ? "opacity-100" : "pointer-events-none opacity-0";
  let t4;
  if ($[0] !== className || $[1] !== t2 || $[2] !== t3) {
    t4 = cn("absolute transition-opacity duration-200 motion-reduce:transition-none", t2, t3, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children || $[5] !== t1 || $[6] !== t4) {
    t5 = <div inert={t1} className={t4}>{children}</div>;
    $[4] = children;
    $[5] = t1;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};

/**
 * 하단 네비게이션 셸 — 웰로 사각 탭바·고기부 pill을 영속 컨테이너 하나가 담고,
 * 탭줄은 활성 레이어 + 퇴장 레이어(크로스페이드 시간만 유예 마운트)의 opacity 전이로
 * 갈아탄다. 셸 형태(위치·높이·radius·그림자)는 전이하지 않고 즉시 갈아입는다 — 그 값들은
 * 레이아웃·재래스터를 매 프레임 일으켜 저사양 기기에서 프레임을 떨어뜨린다(DESIGN.md 「모션」).
 * 상시 마운트가 아니라 2슬롯인 이유: 숨은 레이어도 마운트돼 있으면
 * adpopcorn 인증 쿼리·라우트 prefetch가 전 페이지에서 상시 실행되고 드롭다운 열림 상태가
 * 서비스 복귀 시 되살아난다. 숨김 상태는 마지막 가시 형태를 유지한 채 opacity로만 빠진다.
 */
export const BottomNavShell = () => {
  const $ = _c(38);
  const partnerChromeClassName = usePartnerChromeClassName();
  const pathname = useRenderedPathname();
  const hideBottomNav = useGlobalStore(_temp);
  const isKakaobank = useCheckKakaobank();
  const isGoodsDonationChannel = useDeviceInfoStore(_temp2);
  let t0;
  if ($[0] !== pathname) {
    t0 = getFloatingBnbKind(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isDonationPath = t0 === "donation";
  let t1;
  if ($[2] !== hideBottomNav || $[3] !== isDonationPath || $[4] !== isGoodsDonationChannel || $[5] !== pathname) {
    t1 = () => {
      if (isDonationPath) {
        return isGoodsDonationChannel ? "goods-donation" : "donation";
      }
      if (!hasWelloBottomNav(pathname, hideBottomNav)) {
        return "none";
      }
      return isGoodsDonationChannel ? "goods-donation" : "wello";
    };
    $[2] = hideBottomNav;
    $[3] = isDonationPath;
    $[4] = isGoodsDonationChannel;
    $[5] = pathname;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const getVisibleState = t1;
  const state_0 = hideBottomNav || isKakaobank ? "none" : getVisibleState();
  const [previousState, setPreviousState] = useState(state_0);
  const [exitingState, setExitingState] = useState(null);
  const [lastVisibleState, setLastVisibleState] = useState(state_0 === "none" ? "wello" : state_0);
  if (previousState !== state_0) {
    setPreviousState(state_0);
    setExitingState(previousState === "none" ? null : previousState);
    if (state_0 !== "none") {
      setLastVisibleState(state_0);
    }
  }
  let t2;
  let t3;
  if ($[7] !== exitingState) {
    t2 = () => {
      if (!exitingState) {
        return;
      }
      const unmountTimeoutId = window.setTimeout(() => {
        setExitingState(null);
      }, CROSSFADE_DURATION);
      return () => {
        window.clearTimeout(unmountTimeoutId);
      };
    };
    t3 = [exitingState];
    $[7] = exitingState;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  const shapeState = state_0 === "none" ? lastVisibleState : state_0;
  const shellRef = useRef(null);
  let t4;
  let t5;
  if ($[10] !== state_0) {
    t4 = () => {
      const shell = shellRef.current;
      if (!shell) {
        return;
      }
      const root = document.documentElement;
      if (state_0 === "none") {
        root.style.removeProperty("--bottom-nav-height");
        return;
      }
      const publishOccupiedInset = () => {
        if (shell.offsetHeight === 0) {
          root.style.removeProperty("--bottom-nav-height");
          return;
        }
        root.style.setProperty("--bottom-nav-height", `${Math.round(window.innerHeight - shell.getBoundingClientRect().top)}px`);
      };
      publishOccupiedInset();
      const resizeObserver = new ResizeObserver(publishOccupiedInset);
      resizeObserver.observe(shell);
      window.addEventListener("resize", publishOccupiedInset);
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("resize", publishOccupiedInset);
        root.style.removeProperty("--bottom-nav-height");
      };
    };
    t5 = [state_0];
    $[10] = state_0;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] !== exitingState || $[14] !== state_0) {
    t6 = layer => state_0 === layer || exitingState === layer;
    $[13] = exitingState;
    $[14] = state_0;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const isLayerMounted = t6;
  const t7 = shapeState === "wello" ? WELLO_TAB_BAR_SHAPE_CLASS_NAME : FLOATING_SHAPE_CLASS_NAME;
  const t8 = shapeState === "wello" || !isDonationPath ? "z-10" : "z-20";
  const t9 = state_0 === "none" && "pointer-events-none opacity-0";
  let t10;
  if ($[16] !== partnerChromeClassName || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = cn("fixed hidden select-none border-t-1 bg-Gray-white mobile-tablet:block", "transition-opacity duration-200 motion-reduce:transition-none", t7, t8, t9, partnerChromeClassName);
    $[16] = partnerChromeClassName;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== exitingState || $[22] !== isLayerMounted || $[23] !== state_0) {
    t11 = isLayerMounted("wello") && <BottomNavLayer className="inset-x-0 top-0" isActive={state_0 === "wello"} isEntering={state_0 === "wello" && exitingState !== null}><GlobalNavigation /></BottomNavLayer>;
    $[21] = exitingState;
    $[22] = isLayerMounted;
    $[23] = state_0;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== exitingState || $[26] !== isLayerMounted || $[27] !== state_0) {
    t12 = isLayerMounted("donation") && <BottomNavLayer className="inset-0 flex items-end justify-center" isActive={state_0 === "donation"} isEntering={state_0 === "donation" && exitingState !== null}><ErrorBoundary fallback={null}><DonationBnb /></ErrorBoundary></BottomNavLayer>;
    $[25] = exitingState;
    $[26] = isLayerMounted;
    $[27] = state_0;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== exitingState || $[30] !== isLayerMounted || $[31] !== state_0) {
    t13 = isLayerMounted("goods-donation") && <BottomNavLayer className="inset-0 flex items-end justify-center kbpay:hidden toss:hidden" isActive={state_0 === "goods-donation"} isEntering={state_0 === "goods-donation" && exitingState !== null}><ErrorBoundary fallback={null}><GoodsDonationBnb /></ErrorBoundary></BottomNavLayer>;
    $[29] = exitingState;
    $[30] = isLayerMounted;
    $[31] = state_0;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t12 || $[36] !== t13) {
    t14 = <div ref={shellRef} className={t10}>{t11}{t12}{t13}</div>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
};
function _temp(state) {
  return state.hideBottomNav;
}
function _temp2(deviceState) {
  return hasGoodsDonationHome(deviceState.subDomain ?? null);
}